import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useRouter } from "next/router";
import { selectIsAuthenticated } from "@app/store/authSlice";
import { showBalanceReportModal, showEnterForm, showPaymentIframe } from "@app/store/layoutSlice";
import { AppState } from "@app/store/store";
import {
    useCreateFullReportMutation,
    useCreateReportOrderMutation,
    useGetReportOrderStatusQuery
} from "@entities/history";
import { RouterConfig } from "@shared/lib/routerConfig";
import { BUY_REPORT_MESSAGES, POLLING_INTERVAL } from "./constants";
import { ReportBuyCardProps } from "./types";
import classes from "./report-buy-card.module.scss";

export const ReportBuyCard = ({
    brand,
    model,
    year,
    vin,
    reportProducts,
    slug,
    balance
}: ReportBuyCardProps) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const showPayment = useSelector((state: AppState) => state.layout.paymentIframeVisible);
    const emptyBalance = balance === 0;

    const [orderId, setOrderId] = useState<string | null>(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);

    const [createOrder, { data: orderData }] = useCreateReportOrderMutation();
    const [createFullReport] = useCreateFullReportMutation();
    const { data: orderStatus } = useGetReportOrderStatusQuery(orderId!, {
        skip: !orderId || !paymentSuccess,
        pollingInterval: POLLING_INTERVAL
    });

    const handleBuyClick = async (productUuid: string) => {
        if (!isAuthenticated) {
            dispatch(showEnterForm());
            return;
        }

        try {
            const result = await createOrder({ productUuid }).unwrap();
            setOrderId(result.data.id);
            dispatch(showPaymentIframe());
        } catch (error) {
            toast.error(BUY_REPORT_MESSAGES.ORDER_CREATE_ERROR);
        }
    };

    const handleBuyFromBalance = async () => {
        dispatch(showBalanceReportModal());
    };

    useEffect(() => {
        if (
            showPayment &&
            typeof window !== "undefined" &&
            typeof window.EPayWidget !== "undefined" &&
            orderData &&
            orderData.meta?.payment?.params
        ) {
            const el = document.getElementById("widget-container");
            if (el) el.innerHTML = "";
            const params = orderData.meta.payment.params;

            window.EPayWidget.run(
                {
                    ...params,
                    onPaymentSuccess: () => {
                        setPaymentSuccess(true);
                    },
                    onPaymentFail: () => {
                        toast.error(BUY_REPORT_MESSAGES.PAYMENT_DECLINED);
                    }
                },
                "GET"
            );
        }
    }, [showPayment, orderData]);

    useEffect(() => {
        if (orderStatus?.status === 3) {
            createFullReport({ code: slug })
                .unwrap()
                .then((report) => {
                    router.push(`${RouterConfig.HISTORY}/report/${report.id}`);
                })
                .catch(() => {
                    toast.error(BUY_REPORT_MESSAGES.REPORT_CREATE_ERROR);
                });
        }
    }, [orderStatus]);

    return (
        <div className={classes["report-buy"]}>
            <div className={classes["report-buy__props"]}>
                <h2 className={classes["report-buy__name"]}>
                    {brand} {model}, {year}
                </h2>
                <div className={classes["report-buy__vin"]}>{vin}</div>
            </div>
            {!emptyBalance ? (
                <div className={classes["report-buy__balance"]}>
                    <button
                        className={classes["report-buy__balance-button"]}
                        onClick={handleBuyFromBalance}
                    >
                        Смотреть полный отчёт{" "}
                        <span className={classes["report-buy__remain"]}>
                            осталось {balance}{" "}
                            {balance === 1 ? "отчёт" : balance < 5 ? "отчёта" : "отчётов"}
                        </span>
                    </button>
                </div>
            ) : (
                reportProducts?.length > 0 && (
                    <div className={classes["report-buy__prices"]}>
                        {reportProducts.map((option) => {
                            const countMatch = option.name.match(/\d+/);
                            const count = countMatch ? parseInt(countMatch[0], 10) : 1;
                            const priceRub = option.price / 100;
                            const pricePerPiece = priceRub / count;

                            return (
                                <div key={option.id} className={classes["report-buy__tariff"]}>
                                    <div className={classes["report-buy__count"]}>
                                        {option.name}
                                    </div>
                                    <div className={classes["report-buy__price"]}>
                                        {pricePerPiece}
                                        <div className={classes["report-buy__unit"]}>
                                            ₽
                                            <span className={classes["report-buy__piece"]}>
                                                /шт
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        className={classes["report-buy__button"]}
                                        onClick={() => handleBuyClick(option.id)}
                                    >
                                        Купить за {priceRub}
                                    </button>
                                </div>
                            );
                        })}
                        <span className={classes["report-buy__source"]}>Источник: Автотека</span>
                    </div>
                )
            )}
        </div>
    );
};